import { useContext, useState } from "react";
import { ToastContainer } from "react-toastify";
import classes from "./homePageNew.module.css";
import { getModuleRenderers } from "./moduleRegistry";
import { AuthContext } from "../../context/auth-context";
import { ENTITY_LABELS } from "../../utils/labelMapping";
import { isAssessmentUiOn } from "../../utils/navFlags";

/**
 * Fixed tab order, per product decision — every module gets a tab up front
 * even before it's built, rather than tabs appearing one at a time as each
 * module lands. A tab with no entry in MODULE_RENDERERS (moduleRegistry.js)
 * renders disabled/"coming soon" instead of being hidden.
 *
 * `flag` (optional) — the same AuthContext `user.<flag>` field the sidebar
 * (Component/Sidebar/Sidebar.js) checks before listing that module's own
 * nav item, so a tab here only ever appears where the sidebar would also
 * let the user into that module. Discovery/Brand Audit/Category
 * Analysis/Monitoring have no such flag in the sidebar today (their nav
 * items are unconditional there), so they carry none here either — same
 * "always on" behavior, ported faithfully rather than invented. Category
 * Monitoring's real sidebar flag is `is_category_monitoring_enabled`; wired
 * in below for whenever that module lands, but the tab itself stays
 * commented out for now regardless of the flag's value — it has no
 * MODULE_RENDERERS entry yet, and product asked for it hidden outright
 * rather than shown disabled/"coming soon" in the meantime.
 */
const TABS = [
  { key: "discovery", label: "Discovery" },
  { key: "brandAudit", get label() { return `Brand ${ENTITY_LABELS.audit}`; } },
  { key: "categoryAnalysis", label: "Category Analysis" },
  { key: "monitoring", label: "Monitoring" },
  // { key: "categoryMonitoring", label: "Category Monitoring", flag: "is_category_monitoring_enabled" },
];

// is_assessment_ui_enabled users: Category Analysis runs are listed inside the Assessment tab (getHomePageDataV2,
// BrandAuditV2Module), so there is no separate Category Analysis tab and the tab is just the label.
const ASSESSMENT_UI_TABS = [
  { key: "discovery", label: "Discovery" },
  { key: "brandAudit", get label() { return ENTITY_LABELS.audit; } },
  { key: "monitoring", label: "Monitoring" },
];

/**
 * Each module tab now owns its own data fetch (getHomePageData takes one
 * `modules` value per call — see homePageApi.js's fetchModulePage), so this
 * shell no longer makes a combined first-paint call; it only renders the
 * active tab's module component, which fetches when it mounts. Switching
 * tabs never re-fetches a tab that isn't showing.
 */
export default function HomePageNew() {
  const { user } = useContext(AuthContext);
  const assessmentUi = isAssessmentUiOn(user);
  const MODULE_RENDERERS = getModuleRenderers(assessmentUi);
  const tabs = assessmentUi ? ASSESSMENT_UI_TABS : TABS;
  const visibleTabs = tabs.filter((tab) => !tab.flag || user?.[tab.flag]);
  const [activeTab, setActiveTab] = useState(tabs[0].key);
  // Land on the first tab this user can actually see, e.g. if TABS[0] were
  // ever flag-gated off for them — a no-op today since nothing before
  // Category Monitoring carries a flag.
  const effectiveActiveTab = visibleTabs.some((tab) => tab.key === activeTab) ? activeTab : visibleTabs[0]?.key;
  const ActiveModule = MODULE_RENDERERS[effectiveActiveTab];

  return (
    <div className={classes.main}>
      <div className={classes.tabBar}>
        {visibleTabs.map((tab) => {
          const enabled = !!MODULE_RENDERERS[tab.key];
          return (
            <button
              key={tab.key}
              className={`${classes.tabBtn} ${effectiveActiveTab === tab.key ? classes.tabBtnActive : ""} ${
                !enabled ? classes.tabBtnDisabled : ""
              }`}
              onClick={() => enabled && setActiveTab(tab.key)}
              disabled={!enabled}
              title={enabled ? undefined : "Coming soon"}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {ActiveModule && <ActiveModule key={effectiveActiveTab} />}

      <ToastContainer />
    </div>
  );
}
